import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  HERSTELLER_LISTEN,
  PREISARTEN,
  PREISART_TEXT,
  atelierModul,
  atelierpreiseAlsEingaben,
  atelierpreiseAusEingaben,
  PRODUKTKATEGORIEN,
  filialMargeProzent,
  istAnlegbar,
  listenTitel,
  type Hersteller,
  type HerstellerArt,
  type AtelierEingaben,
  type AtelierFehler,
  type AtelierModul,
  type Atelierpreise,
  type Preisart,
  type Produktkategorie,
} from "@repo/shared";

import {
  AnsprechpartnerEditor,
  alsEntwurf,
  ausEntwurf,
  istLeer,
  type AnsprechpartnerEntwurf,
} from "../components/AnsprechpartnerEditor";
import { AtelierpreiseAnsicht, AtelierpreiseEingabe } from "../components/hersteller/Atelierpreise";
import { Bestaetigen } from "../components/stoffe/Bestaetigen";
import { api, errorMessage } from "../lib/api";
import { Kategorien, Preisquelle } from "./HerstellerPage";

type Formular = {
  name: string;
  art: HerstellerArt;
  /** Preiseditor – nur bei Nähateliers aus dem Code. */
  atelier: AtelierEingaben | null;
  kuerzel: string;
  beschreibung: string;
  website: string;
  email: string;
  telefon: string;
  fax: string;
  strasse: string;
  plz: string;
  ort: string;
  land: string;
  standardLieferzeitTage: string;
  kategorien: Produktkategorie[];
  kundennummer: string;
  aufschlagProzent: string;
  preisart: Preisart;
  rabattProzent: string;
  teuerungProzent: string;
  notiz: string;
  ansprechpartner: AnsprechpartnerEntwurf[];
};

type Feldfehler = Partial<Record<keyof Formular, string>>;

const istKategorie = (k: string): k is Produktkategorie => (PRODUKTKATEGORIEN as readonly string[]).includes(k);

/** Das Atelier-Modul eines Nähateliers aus dem Code; sonst undefined. */
function atelierVon(h: Hersteller): AtelierModul | undefined {
  return h.art === "naehatelier" ? atelierModul(h.schluessel) : undefined;
}

function formularAus(h: Hersteller): Formular {
  const modul = atelierVon(h);
  return {
    name: h.name,
    art: h.art,
    atelier: modul ? atelierpreiseAlsEingaben(modul, h.atelierpreise) : null,
    kuerzel: h.kuerzel ?? "",
    beschreibung: h.beschreibung ?? "",
    website: h.website ?? "",
    email: h.email ?? "",
    telefon: h.telefon ?? "",
    fax: h.fax ?? "",
    strasse: h.anschrift?.strasse ?? "",
    plz: h.anschrift?.plz ?? "",
    ort: h.anschrift?.ort ?? "",
    land: h.anschrift?.land ?? "",
    standardLieferzeitTage: h.standardLieferzeitTage === null ? "" : String(h.standardLieferzeitTage),
    kategorien: h.kategorien.filter(istKategorie),
    kundennummer: h.kundennummer ?? "",
    aufschlagProzent: String(h.aufschlagProzent).replace(".", ","),
    preisart: h.preisart,
    rabattProzent: String(h.rabattProzent).replace(".", ","),
    teuerungProzent: String(h.teuerungProzent).replace(".", ","),
    notiz: h.notiz ?? "",
    ansprechpartner: h.ansprechpartner.map(alsEntwurf),
  };
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Prozent aus der Eingabe („12,5“), auf zwei Stellen gerundet – so geht er auch an die API. */
function prozentAus(text: string): number {
  return Math.round(Number(text.replace(",", ".")) * 100) / 100;
}

function gueltigesProzent(text: string): boolean {
  const wert = prozentAus(text);
  return text.trim() !== "" && Number.isFinite(wert) && wert >= 0 && wert <= 99.99;
}

/** Marge der Filiale aus den Eingaben – live beim Bearbeiten; null, solange eine Eingabe ungültig ist. */
function margeAus(f: Formular): number | null {
  const aufschlag = prozentAus(f.aufschlagProzent);
  if (!gueltigesProzent(f.rabattProzent)) return null;
  if (!f.aufschlagProzent.trim() || !Number.isFinite(aufschlag) || aufschlag < 0) return null;
  return filialMargeProzent({ preisart: f.preisart, rabattProzent: prozentAus(f.rabattProzent), aufschlagProzent: aufschlag });
}

/** „46,1 %“ – ohne bestimmbare Marge „—“. */
function margeText(marge: number | null): string {
  return marge === null ? "—" : `${marge.toLocaleString("de-DE", { maximumFractionDigits: 1 })} %`;
}

/** Prüft im Browser, was die API ohnehin ablehnen würde – mit dem Fehler direkt am Feld. */
function pruefe(
  f: Formular,
  nameAenderbar: boolean,
  mitKonditionen: boolean,
  mitTeuerung: boolean,
  modul: AtelierModul | undefined,
): {
  felder: Feldfehler;
  partner: Record<string, string>;
  atelierFehler: AtelierFehler;
  atelierpreise: Atelierpreise | null;
} {
  const felder: Feldfehler = {};
  if (nameAenderbar && !f.name.trim()) felder.name = "Bitte einen Namen eintragen.";
  if (f.email.trim() && !EMAIL.test(f.email.trim())) felder.email = "Keine gültige E-Mail-Adresse.";
  if (f.land.trim() && !/^[A-Za-z]{2}$/.test(f.land.trim())) felder.land = "Zwei Buchstaben, z. B. DE.";
  const tage = f.standardLieferzeitTage.trim();
  if (tage && !(/^\d+$/.test(tage) && Number(tage) <= 365)) {
    felder.standardLieferzeitTage = "Ganze Tage von 0 bis 365.";
  }
  const aufschlag = Number(f.aufschlagProzent.replace(",", "."));
  // Die Spalte ist numeric(5,2): Gerundet auf zwei Stellen darf der Wert höchstens 999,99 sein.
  if (
    !f.aufschlagProzent.trim() ||
    !Number.isFinite(aufschlag) ||
    aufschlag < 0 ||
    Math.round(aufschlag * 100) / 100 > 999.99
  ) {
    felder.aufschlagProzent = "0 bis 999,99 %.";
  }
  if (mitTeuerung && !gueltigesProzent(f.teuerungProzent)) felder.teuerungProzent = "0 bis 99,99 %.";
  if (mitKonditionen && !gueltigesProzent(f.rabattProzent)) felder.rabattProzent = "0 bis 99,99 %.";
  const partner: Record<string, string> = {};
  for (const p of f.ansprechpartner) {
    if (istLeer(p)) continue;
    if (!p.nachname.trim()) partner[p.kennung] = "Nachname fehlt.";
    else if (p.email.trim() && !EMAIL.test(p.email.trim())) partner[p.kennung] = "Keine gültige E-Mail-Adresse.";
  }
  // Die Preisliste zählt nur bei Nähateliers aus dem Code.
  const { preise, fehler: atelierFehler } =
    modul && f.atelier ? atelierpreiseAusEingaben(modul, f.atelier) : { preise: null, fehler: {} };
  return { felder, partner, atelierFehler, atelierpreise: preise };
}

/** Link nur für http(s); ohne Schema gilt https. Alles andere erscheint als Text. */
function websiteLink(website: string): string | null {
  const mitSchema = /^[a-z][a-z\d+.-]*:/i.test(website) ? website : `https://${website}`;
  try {
    const url = new URL(mitSchema);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

/** Stammdaten eines Herstellers ansehen und bearbeiten. */
export function HerstellerDetailPage() {
  const { schluessel } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const bearbeitenGewuenscht = (location.state as { bearbeiten?: boolean } | null)?.bearbeiten === true;
  const [hersteller, setHersteller] = useState<Hersteller | null>(null);
  const [form, setForm] = useState<Formular | null>(null);
  const [feldfehler, setFeldfehler] = useState<Feldfehler>({});
  const [partnerfehler, setPartnerfehler] = useState<Record<string, string>>({});
  const [atelierfehler, setAtelierfehler] = useState<AtelierFehler>({});
  const [loeschenFrage, setLoeschenFrage] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const laden = useCallback(async () => {
    if (!schluessel) return;
    try {
      const res = await api.api.admin.hersteller[":id"].$get({ param: { id: schluessel } });
      if (!res.ok) throw new Error(await errorMessage(res, "Hersteller nicht gefunden"));
      const daten = await res.json();
      if ("hersteller" in daten) setHersteller(daten.hersteller);
      setFehler(null);
    } catch (cause) {
      setFehler(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
    }
  }, [schluessel]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // Frisch angelegt: direkt bearbeiten. Nur einmal – Neuladen oder Zurück zeigt wieder die Ansicht.
  useEffect(() => {
    if (!hersteller || !bearbeitenGewuenscht) return;
    setForm(formularAus(hersteller));
    navigate(location.pathname, { replace: true, state: null });
  }, [hersteller, bearbeitenGewuenscht, navigate, location.pathname]);

  // Das Formular ist länger als der Bildschirm und noValidate schaltet den Sprung des
  // Browsers zum ersten ungültigen Feld ab: Nach einem Fehler zur ersten Meldung springen.
  useEffect(() => {
    document.querySelector("form small.error")?.scrollIntoView({ block: "center" });
  }, [feldfehler, partnerfehler, atelierfehler]);

  useEffect(() => {
    if (fehler) document.querySelector("main p.error")?.scrollIntoView({ block: "center" });
  }, [fehler]);

  async function speichern(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form || !schluessel) return;
    setFehler(null);
    const nameAenderbar = hersteller !== null && !hersteller.hatKonfigurator;
    // Kommt der Rabatt je Artikel vom Hersteller (Somfy), sind Preisart und Rabatt nicht pflegbar.
    const mitKonditionen = hersteller?.art === "konfigurator" && !hersteller.rabattDynamisch;
    // Den Teuerungszuschlag pflegen alle Konfigurator-Hersteller, auch die mit dynamischem Rabatt.
    const mitTeuerung = hersteller?.art === "konfigurator";
    const { felder, partner, atelierFehler, atelierpreise } = pruefe(
      form,
      nameAenderbar,
      mitKonditionen,
      mitTeuerung,
      hersteller ? atelierVon(hersteller) : undefined,
    );
    setFeldfehler(felder);
    setPartnerfehler(partner);
    setAtelierfehler(atelierFehler);
    if (Object.keys(felder).length > 0 || Object.keys(partner).length > 0 || Object.keys(atelierFehler).length > 0) return;

    const text = (w: string) => (w.trim() === "" ? null : w.trim());
    setSpeichert(true);
    try {
      const res = await api.api.admin.hersteller[":id"].$patch({
        param: { id: schluessel },
        json: {
          ...(nameAenderbar ? { name: form.name.trim() } : {}),
          ...(hersteller && form.art !== hersteller.art && istAnlegbar(form.art) ? { art: form.art } : {}),
          ...(atelierpreise ? { atelierpreise } : {}),
          kuerzel: text(form.kuerzel),
          beschreibung: text(form.beschreibung),
          website: text(form.website),
          email: text(form.email),
          telefon: text(form.telefon),
          fax: text(form.fax),
          strasse: text(form.strasse),
          plz: text(form.plz),
          ort: text(form.ort),
          land: text(form.land)?.toUpperCase() ?? null,
          standardLieferzeitTage: form.standardLieferzeitTage.trim() ? Number(form.standardLieferzeitTage) : null,
          kategorien: form.kategorien,
          kundennummer: text(form.kundennummer),
          aufschlagProzent: Number(form.aufschlagProzent.replace(",", ".")),
          ...(mitKonditionen ? { preisart: form.preisart, rabattProzent: prozentAus(form.rabattProzent) } : {}),
          ...(mitTeuerung ? { teuerungProzent: prozentAus(form.teuerungProzent) } : {}),
          notiz: text(form.notiz),
          ansprechpartner: form.ansprechpartner.filter((p) => !istLeer(p)).map(ausEntwurf),
        },
      });
      if (!res.ok) throw new Error(await errorMessage(res, "Speichern fehlgeschlagen"));
      const daten = await res.json();
      if ("hersteller" in daten) setHersteller(daten.hersteller);
      setForm(null);
      setFehler(null);
    } catch (cause) {
      setFehler(cause instanceof Error ? cause.message : "Speichern fehlgeschlagen");
    } finally {
      setSpeichert(false);
    }
  }

  async function loeschen() {
    if (!hersteller) return;
    try {
      const res = await api.api.admin.hersteller[":id"].$delete({ param: { id: hersteller.schluessel } });
      if (!res.ok) throw new Error(await errorMessage(res, "Löschen fehlgeschlagen"));
      navigate(`/hersteller?liste=${hersteller.art}`, { replace: true });
    } catch (cause) {
      setLoeschenFrage(false);
      setFehler(cause instanceof Error ? cause.message : "Löschen fehlgeschlagen");
    }
  }

  if (!hersteller) {
    return fehler ? <p className="error">{fehler}</p> : <p className="muted">Lade Hersteller …</p>;
  }

  const h = hersteller;
  const modul = atelierVon(h);
  const kopf = (
    <>
      <p className="breadcrumb">
        <Link to="/hersteller">Hersteller</Link> /{" "}
        <Link to={`/hersteller?liste=${h.art}`}>{listenTitel(h.art)}</Link> / {h.name}
      </p>
      <h1>
        {h.name}
        {h.serverAnbindung && (
          <>
            {" "}
            <span className="badge badge--active">Serverangebunden · {h.serverAnbindung}</span>
          </>
        )}
      </h1>
      <p className="muted small">
        <code>{h.schluessel}</code> · <Preisquelle quelle={h.quelle} />
      </p>
      {h.serverAnbindung && (
        <p className="muted small">
          Farben, Farbbilder und Verfügbarkeit kommen über das {h.serverAnbindung}-Portal. Der Hersteller ist im Code
          angebunden und lässt sich nicht löschen.
        </p>
      )}
      {!h.gelistet && (
        <p className="muted small">
          Dieser Konfigurator rechnet mit der Preisliste des gewählten Nähateliers (Hersteller → Nähateliers). Preisart,
          Rabatt und Teuerung wirken hier nicht; der Aufschlag gilt nur, wenn kein Atelier eine Preisliste liefert.
          Deshalb steht er in keiner Herstellerliste.
        </p>
      )}
      {h.quelle === "ohne" && (
        <p className="muted small">
          Ohne aktiven Konfigurator: Mandanten sehen für diesen Hersteller keinen Konfigurator.
        </p>
      )}
      {fehler && <p className="error">{fehler}</p>}
    </>
  );

  if (!form) {
    const anschrift = h.anschrift
      ? [h.anschrift.strasse, [h.anschrift.plz, h.anschrift.ort].filter(Boolean).join(" "), h.anschrift.land]
          .filter(Boolean)
          .join(", ")
      : null;
    const link = h.website ? websiteLink(h.website) : null;
    return (
      <section>
        {kopf}
        <div className="formular-raster">
          <fieldset>
            <legend>Kontakt</legend>
            <dl className="info">
              <dt>Kürzel</dt>
              <dd>{h.kuerzel ?? "—"}</dd>
              <dt>E-Mail</dt>
              <dd>{h.email ? <a href={`mailto:${h.email}`}>{h.email}</a> : "—"}</dd>
              <dt>Telefon</dt>
              <dd>{h.telefon ?? "—"}</dd>
              <dt>Fax</dt>
              <dd>{h.fax ?? "—"}</dd>
              <dt>Website</dt>
              <dd>
                {link ? (
                  <a href={link} target="_blank" rel="noreferrer">
                    {h.website}
                  </a>
                ) : (
                  (h.website ?? "—")
                )}
              </dd>
              <dt>Anschrift</dt>
              <dd>{anschrift ?? "—"}</dd>
            </dl>
          </fieldset>
          <fieldset>
            {/* Ateliers nähen nur – Produktkategorien gibt es bei ihnen nicht. */}
            <legend>{modul ? "Lieferung" : "Sortiment und Lieferung"}</legend>
            <dl className="info">
              {!modul && (
                <>
                  <dt>Kategorien</dt>
                  <dd>
                    <Kategorien liste={h.kategorien} />
                  </dd>
                </>
              )}
              <dt>Standard-Lieferzeit</dt>
              <dd>{h.standardLieferzeitTage === null ? "—" : `${h.standardLieferzeitTage} Tage`}</dd>
              <dt>Beschreibung</dt>
              <dd>{h.beschreibung ?? "—"}</dd>
            </dl>
          </fieldset>
          <fieldset>
            <legend>Konditionen</legend>
            <dl className="info">
              <dt>Kundennummer</dt>
              <dd>{h.kundennummer ?? "—"}</dd>
              <dt>{modul ? "ERP-Aufschlag" : "Aufschlag (Marge)"}</dt>
              <dd>{h.aufschlagProzent.toLocaleString("de-DE")} %</dd>
              {h.art === "konfigurator" && (
                <>
                  <dt>Preisart</dt>
                  <dd>{PREISART_TEXT[h.preisart]}</dd>
                  <dt>Rabatt des Herstellers</dt>
                  <dd>{h.rabattDynamisch ? "dynamisch" : `${h.rabattProzent.toLocaleString("de-DE")} %`}</dd>
                  <dt>Teuerungszuschlag</dt>
                  <dd>{h.teuerungProzent.toLocaleString("de-DE")} %</dd>
                  {!h.rabattDynamisch && (
                    <>
                      <dt>Marge der Filiale auf die UVP</dt>
                      <dd>{margeText(filialMargeProzent(h))}</dd>
                    </>
                  )}
                </>
              )}
              <dt>Notiz</dt>
              <dd>{h.notiz ?? "—"}</dd>
            </dl>
            {h.art === "konfigurator" &&
              (h.rabattDynamisch ? (
                <p className="muted small">Der Rabatt kommt je Artikel live vom Hersteller und passt sich dynamisch an.</p>
              ) : (
                <p className="muted small">Bei VK: Hersteller-EK = Preis × (1 − Rabatt). Bei EK netto: UVP des Herstellers (brutto) = EK ÷ (1 − Rabatt).</p>
              ))}
          </fieldset>
        </div>

        {modul && <AtelierpreiseAnsicht modul={modul} preise={h.atelierpreise} erpAufschlagProzent={h.aufschlagProzent} />}

        <h2>Ansprechpartner ({h.ansprechpartner.length})</h2>
        {h.ansprechpartner.length === 0 ? (
          <p className="muted">Keine Ansprechpartner – über „Bearbeiten“ anlegen.</p>
        ) : (
          <div className="tabelle-rahmen">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Funktion</th>
                  <th>E-Mail</th>
                  <th>Telefon</th>
                  <th>Mobil</th>
                </tr>
              </thead>
              <tbody>
                {h.ansprechpartner.map((p, i) => (
                  <tr key={`${p.nachname}-${i}`}>
                    <td>
                      {[p.vorname, p.nachname].filter(Boolean).join(" ")}
                      {i === 0 && <div className="muted small">Kontaktperson</div>}
                    </td>
                    <td>{p.funktion ?? "—"}</td>
                    <td>{p.email ? <a href={`mailto:${p.email}`}>{p.email}</a> : "—"}</td>
                    <td>{p.telefon ?? "—"}</td>
                    <td>{p.mobil ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="actions actions--abgesetzt">
          <button type="button" className="primary" onClick={() => setForm(formularAus(h))}>
            Bearbeiten
          </button>
          {h.art !== "konfigurator" && !modul && !h.serverAnbindung && (
            <button type="button" className="stoffe-knopf--gefahr" onClick={() => setLoeschenFrage(true)}>
              Löschen
            </button>
          )}
        </div>

        <Bestaetigen
          offen={loeschenFrage}
          titel="Hersteller löschen?"
          text={`„${h.name}“ wird gelöscht und verschwindet aus allen Listen. Das lässt sich im Dashboard nicht rückgängig machen.`}
          bestaetigen="Löschen"
          gefaehrlich
          onBestaetigen={loeschen}
          onAbbrechen={() => setLoeschenFrage(false)}
        />
      </section>
    );
  }

  const marge = margeAus(form);

  const feld = (
    schluesselFeld: Exclude<keyof Formular, "kategorien" | "ansprechpartner" | "art" | "atelier" | "preisart">,
    label: string,
    extra: React.InputHTMLAttributes<HTMLInputElement> = {},
  ) => (
    <label className="field">
      <span>{label}</span>
      <input
        value={form[schluesselFeld]}
        onChange={(e) => setForm({ ...form, [schluesselFeld]: e.target.value })}
        {...extra}
      />
      {feldfehler[schluesselFeld] && <small className="error">{feldfehler[schluesselFeld]}</small>}
    </label>
  );

  return (
    <section>
      {kopf}
      <form onSubmit={speichern} noValidate>
        <div className="formular-raster">
          <fieldset>
            <legend>Stammdaten</legend>
            {!h.hatKonfigurator ? (
              feld("name", "Name", { maxLength: 255 })
            ) : (
              <label className="field">
                <span>Name</span>
                <input value={h.name} disabled />
                <small className="muted">Kommt vom Konfigurator und lässt sich hier nicht ändern.</small>
              </label>
            )}
            <label className="field">
              <span>Liste</span>
              {!h.hatKonfigurator && !modul && !h.serverAnbindung ? (
                <>
                  <select value={form.art} onChange={(e) => setForm({ ...form, art: e.target.value as HerstellerArt })}>
                    {HERSTELLER_LISTEN.filter((l) => istAnlegbar(l.art) || l.art === h.art).map((l) => (
                      <option key={l.art} value={l.art} disabled={!istAnlegbar(l.art)}>
                        {l.titel}
                      </option>
                    ))}
                  </select>
                  <small className="muted">Wo der Hersteller im Dashboard erscheint – ändert keine Preise.</small>
                </>
              ) : (
                <>
                  <input value={listenTitel(h.art)} disabled />
                  <small className="muted">
                    {modul
                      ? "Nähateliers werden im Code angelegt."
                      : h.serverAnbindung
                        ? "Serverangebundene Hersteller bleiben in der Liste Stoffverlage."
                        : "Ergibt sich aus dem Konfigurator."}
                  </small>
                </>
              )}
            </label>
            {feld("kuerzel", "Kürzel", { maxLength: 64 })}
            <label className="field">
              <span>Beschreibung</span>
              <textarea
                rows={3}
                value={form.beschreibung}
                onChange={(e) => setForm({ ...form, beschreibung: e.target.value })}
              />
            </label>
          </fieldset>
          <fieldset>
            <legend>Kontakt</legend>
            {feld("email", "E-Mail", { type: "email", maxLength: 255 })}
            {feld("telefon", "Telefon", { maxLength: 64 })}
            {feld("fax", "Fax", { maxLength: 64 })}
            {feld("website", "Website", { type: "url", placeholder: "https://", maxLength: 255 })}
          </fieldset>
          <fieldset>
            <legend>Anschrift</legend>
            {feld("strasse", "Straße und Hausnummer", { maxLength: 255 })}
            {feld("plz", "PLZ", { maxLength: 16 })}
            {feld("ort", "Ort", { maxLength: 255 })}
            {feld("land", "Land (ISO, z. B. DE)", { maxLength: 2 })}
          </fieldset>
          <fieldset>
            <legend>{modul ? "Lieferung" : "Sortiment und Lieferung"}</legend>
            {feld("standardLieferzeitTage", "Standard-Lieferzeit (Tage)", { inputMode: "numeric" })}
            {!modul && (
              <div className="kategorien-auswahl">
                {PRODUKTKATEGORIEN.map((k) => (
                  <label key={k} className="field field--inline">
                    <input
                      type="checkbox"
                      checked={form.kategorien.includes(k)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          kategorien: e.target.checked ? [...form.kategorien, k] : form.kategorien.filter((x) => x !== k),
                        })
                      }
                    />
                    <span>{k}</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>
          <fieldset>
            <legend>Konditionen</legend>
            {feld("kundennummer", "Kundennummer bei Hersteller", { maxLength: 64 })}
            {!modul &&
              feld("aufschlagProzent", "Aufschlag % – Marge auf den Hersteller-EK, ergibt den EK der Filialen", {
                inputMode: "decimal",
              })}
            {h.art === "konfigurator" && h.rabattDynamisch && (
              <>
                <label className="field">
                  <span>Preisart</span>
                  <input value={PREISART_TEXT[h.preisart]} disabled />
                </label>
                <label className="field">
                  <span>Rabatt des Herstellers (%)</span>
                  <input value="dynamisch" disabled />
                  <small className="muted">Kommt je Artikel live vom Hersteller und lässt sich hier nicht ändern.</small>
                </label>
              </>
            )}
            {h.art === "konfigurator" && !h.rabattDynamisch && (
              <>
                <label className="field">
                  <span>Preisart</span>
                  <select value={form.preisart} onChange={(e) => setForm({ ...form, preisart: e.target.value as Preisart })}>
                    {PREISARTEN.map((p) => (
                      <option key={p} value={p}>
                        {PREISART_TEXT[p]}
                      </option>
                    ))}
                  </select>
                  <small className="muted">
                    Wie der Hersteller seine Preise liefert – Preisliste (PDF) oder API. Bei VK: Hersteller-EK = Preis × (1 − Rabatt).
                    Bei EK netto: UVP des Herstellers (brutto) = EK ÷ (1 − Rabatt).
                  </small>
                </label>
                {feld("rabattProzent", "Rabatt des Herstellers (%)", { inputMode: "decimal" })}
              </>
            )}
            {h.art === "konfigurator" && (
              <label className="field">
                <span>Teuerungszuschlag (%)</span>
                <input
                  value={form.teuerungProzent}
                  inputMode="decimal"
                  onChange={(e) => setForm({ ...form, teuerungProzent: e.target.value })}
                />
                <small className="muted">
                  Auf die Liste – bei VK auf den gelieferten Preis, bei EK netto auf die UVP des Herstellers. Der Rabatt
                  gilt auch auf den Zuschlag: Hersteller-EK = Liste × (1 + Teuerung) × (1 − Rabatt).
                </small>
                {feldfehler.teuerungProzent && <small className="error">{feldfehler.teuerungProzent}</small>}
              </label>
            )}
            {h.art === "konfigurator" && !h.rabattDynamisch && (
              <label className="field">
                <span>Marge der Filiale auf die Hersteller-UVP</span>
                <input value={margeText(marge)} disabled />
                <small className="muted">
                  {marge === null
                    ? "Erscheint, sobald Rabatt und Aufschlag gültig sind – bei EK netto nur mit Rabatt. "
                    : `Auf 100 € UVP inkl. Teuerung zahlt die Filiale ${(100 - marge).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € EK. `}
                  Marge = 1 − (1 − Rabatt) × (1 + Aufschlag): Ohne Aufschlag ist sie der Rabatt, der Teuerungszuschlag ändert
                  sie nicht.
                </small>
              </label>
            )}
            <label className="field">
              <span>Notiz</span>
              <textarea
                rows={3}
                maxLength={5000}
                value={form.notiz}
                onChange={(e) => setForm({ ...form, notiz: e.target.value })}
              />
            </label>
          </fieldset>
        </div>

        {modul && form.atelier && (
          <AtelierpreiseEingabe
            modul={modul}
            werte={form.atelier}
            fehler={atelierfehler}
            erpAufschlag={form.aufschlagProzent}
            erpFehler={feldfehler.aufschlagProzent}
            onChange={(atelier) => setForm({ ...form, atelier })}
            onErpAufschlag={(aufschlagProzent) => setForm({ ...form, aufschlagProzent })}
          />
        )}

        <AnsprechpartnerEditor
          liste={form.ansprechpartner}
          fehler={partnerfehler}
          onChange={(ansprechpartner) => setForm({ ...form, ansprechpartner })}
        />

        <div className="actions">
          <button type="submit" className="primary" disabled={speichert}>
            {speichert ? "Speichert …" : "Speichern"}
          </button>
          <button
            type="button"
            disabled={speichert}
            onClick={() => {
              setForm(null);
              setFeldfehler({});
              setPartnerfehler({});
              setAtelierfehler({});
              setFehler(null);
            }}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </section>
  );
}
